import React from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { Button, ButtonVariant } from "shared/ui/buttons/Button"
import { Chip } from "shared/ui/chip/Chip"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import { ProjectPhaseChip } from "entities/projects/ui/project-phase-chip/ProjectPhaseChip"
import { ProjectRoleChip } from "entities/projects/ui/project-role-chip/ProjectRoleChip"
import { ProjectActionType } from "features/project-management/types/ProjectActionType"
import { ProjectActionsButton } from "features/project-management/ui/project-actions-button/ProjectActionsButton"
import { ProjectCard } from "features/project-management/ui/project-card/ProjectCard"

import { ProjectHeaderProps } from "../ProjectHeader"
import { ProjectHeaderLoader } from "./ProjectHeaderLoader"

export const projectHeaderAllowedActions = [
  ProjectActionType.EDIT,
  ProjectActionType.ADD_CLARIFICATION_REQUEST,
  ProjectActionType.EXPORT,
  ProjectActionType.GAEB_EXPORT,
  ProjectActionType.MUSTERHEFT_EXPORT,
  ProjectActionType.ATTACHMENTS,
  ProjectActionType.SHARE,
  ProjectActionType.MANAGE_TEAM,
  ProjectActionType.LEAVE_TEAM,
  ProjectActionType.INVITE_MEMBER,
  ProjectActionType.DUPLICATE,
  ProjectActionType.ACTIVATE,
  ProjectActionType.HISTORY,
  ProjectActionType.DELETE
]

export const ProjectHeaderView = React.memo(
  ({
    projectModel,
    isLoading: propsIsLoading,
    additionalActions,
    className,

    onClick
  }: ProjectHeaderProps) => {
    const { t } = useTranslation()
    const permissions = projectModel?.core.data.permissions?.data
    const role = projectModel?.core.data.role?.data
    const isLoading =
      projectModel?.core.data.project?.isLoading || propsIsLoading
    const isPending = projectModel?.core.data.meta?.isPending
    const isLoadingPermissions = projectModel?.core.data.permissions?.isLoading

    const showAttachmentCount =
      !!projectModel?.attachments.data.attachments?.data?.length

    if (isLoading || propsIsLoading) {
      return <ProjectHeaderLoader />
    }

    return (
      <ProjectCard
        className={classNames(
          "card--elevated",
          "project-card--header",
          "project-card--compact",
          { "project-card--clickable": !!onClick },
          className
        )}
        model={projectModel}
        isLoading={isLoading}
        as="section"
        onClick={onClick}
      >
        <ProjectCard.Image />

        <ProjectCard.Title as="h1" showClarificationRequestIndicator />
        <ProjectCard.Actions>
          <Button
            label={t("Project.Actions.Attachments")}
            onClick={
              projectModel?.attachments.handlers.onOpenAttachments ?? undefined
            }
            variant={ButtonVariant.SECONDARY}
            className={classNames(
              { "btn--icon-only": !showAttachmentCount },
              "project-card__attachments-button"
            )}
          >
            <Icon name={IconName.PAPERCLIP} />
            {showAttachmentCount ? (
              <Chip className="chip--count">
                {projectModel?.attachments.data.attachments?.data?.length ?? 0}
              </Chip>
            ) : null}
          </Button>
          {projectModel?.core.handlers.onShowDetails ? (
            <Button
              label={t("Project.Actions.ShowDetails")}
              onClick={projectModel?.core.handlers.onShowDetails}
              isIconOnly
              icon={IconName.ZOOM_IN}
              variant={ButtonVariant.SECONDARY}
            />
          ) : null}
          {permissions ? (
            <ProjectActionsButton
              onOpen={projectModel?.core.handlers.onOpen}
              onEdit={projectModel?.core.handlers.onEdit}
              onAddClarificationRequest={
                projectModel?.core.handlers.onAddClarificationRequest
              }
              onShowDetails={projectModel?.core.handlers.onShowDetails}
              onShare={projectModel?.core.handlers.onShare}
              onManageTeam={projectModel?.team.handlers.onManageTeam}
              onInviteMember={projectModel?.team.handlers.onInviteMember}
              onLeaveTeam={projectModel?.team.handlers.onLeaveTeam}
              onExport={projectModel?.core.handlers.onExport}
              onGaebExport={projectModel?.core.handlers.onGaebExport}
              onMusterheftExport={
                projectModel?.core.handlers.onMusterheftExport
              }
              onOpenAttachments={
                projectModel?.attachments.handlers.onOpenAttachments
              }
              onNewProduct={projectModel?.creation?.handlers.onNewProduct}
              onNewRoom={projectModel?.creation?.handlers.onNewRoom}
              onNewFloor={projectModel?.creation?.handlers.onNewFloor}
              onDelete={projectModel?.core.handlers.onDelete}
              onActivate={projectModel?.core.handlers.onActivate}
              onDuplicate={projectModel?.core.handlers.onDuplicate}
              onOpenProjectHistory={
                projectModel.core.handlers.onOpenProjectHistory
              }
              additionalActions={additionalActions}
              className="project-card__actions-button"
              isDisabled={isLoading || isLoadingPermissions || isPending}
              allowedActions={projectHeaderAllowedActions}
            />
          ) : null}
        </ProjectCard.Actions>
        <ProjectCard.Chips>
          <ProjectPhaseChip
            phase={projectModel?.core.data.project?.data?.phase}
            isLoading={isLoading}
          />
          <ProjectRoleChip teamRole={role} isLoading={isLoading} />
        </ProjectCard.Chips>
      </ProjectCard>
    )
  }
)
